/** Shared MUI sx tokens aligned with landing page */

export const HH_COLORS = {
  bg: 'var(--hh-bg, #F9F8F5)',
  fg: 'var(--hh-fg, #0D0D0D)',
  fg2: 'var(--hh-fg2, #3A3A3A)',
  fg3: 'var(--hh-fg3, #707070)',
  teal: 'var(--hh-teal, #5BB5C7)',
  card: 'var(--hh-card-bg, #FFFFFF)',
  cardBorder: 'var(--hh-card-border, rgba(0, 0, 0, 0.08))',
  surfaceMuted: 'var(--hh-surface-muted, #F3F1EC)',
  surfaceAccent: 'var(--hh-surface-accent, #EDE9E3)',
  inset: 'var(--hh-marketing-inset-bg, var(--hh-surface-muted, #F3F1EC))',
  track: 'var(--hh-track-bg, #E5E2DC)',
  inputBg: 'var(--hh-input-bg, #FFFFFF)',
  inputFg: 'var(--hh-input-fg, #0D0D0D)',
  cardShadow: 'var(--hh-marketing-card-shadow, none)',
};

export const hhPageSx = {
  minHeight: 'calc(100vh - 68px)',
  background: HH_COLORS.bg,
  color: HH_COLORS.fg,
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  pt: { xs: 6, md: 8 },
  pb: { xs: 8, md: 10 },
};

export const hhHeroEyebrowSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: HH_COLORS.teal,
  mb: 2,
  '&::before': {
    content: '""',
    display: 'block',
    width: '22px',
    height: '1.5px',
    background: 'currentColor',
  },
};

export const hhHeroTitleSx = {
  fontFamily: "'Playfair Display', serif",
  fontWeight: 700,
  fontSize: { xs: '2.25rem', md: '3.25rem' },
  lineHeight: 1.05,
  letterSpacing: '-0.032em',
  color: HH_COLORS.fg,
  mb: 2,
};

export const hhHeroSubtitleSx = {
  color: HH_COLORS.fg3,
  maxWidth: '640px',
  mx: 'auto',
  lineHeight: 1.6,
  fontSize: { xs: '1rem', md: '1.125rem' },
  fontWeight: 400,
};

export const hhPrimaryButtonSx = {
  px: 4,
  py: 1.5,
  background: HH_COLORS.fg,
  color: HH_COLORS.bg,
  fontSize: '0.95rem',
  fontWeight: 700,
  borderRadius: '999px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    background: HH_COLORS.fg2,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
};

export const hhOutlinedButtonSx = {
  px: 4,
  py: 1.5,
  color: HH_COLORS.fg3,
  borderColor: HH_COLORS.cardBorder,
  fontSize: '0.95rem',
  fontWeight: 600,
  borderRadius: '999px',
  textTransform: 'none',
  '&:hover': {
    color: HH_COLORS.fg,
    borderColor: HH_COLORS.fg3,
    backgroundColor: 'transparent',
  },
};

export const hhCardPaperSx = {
  backgroundColor: HH_COLORS.card,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
};

/** Refined marketing cards — border-first, token-driven surfaces */
export const hhMarketingCardSx = {
  backgroundColor: HH_COLORS.card,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: '20px',
  boxShadow: HH_COLORS.cardShadow,
  overflow: 'hidden',
};

export const hhMarketingStatCardSx = {
  ...hhMarketingCardSx,
  p: { xs: 2.5, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1.5,
  textAlign: 'left',
};

export const hhMarketingStatIconSx = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  background: 'color-mix(in srgb, var(--hh-teal) 14%, var(--hh-marketing-inset-bg))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: HH_COLORS.teal,
  fontSize: '1.1rem',
};

export const hhMarketingStepCardSx = {
  ...hhMarketingCardSx,
  height: '100%',
  cursor: 'pointer',
  position: 'relative',
};

export const hhMarketingStepVisualSx = {
  mb: 2.5,
  p: 2,
  borderRadius: '16px',
  background: HH_COLORS.inset,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  minHeight: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const hhMarketingStepMiniCardSx = {
  background: HH_COLORS.card,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: 2,
  boxShadow: HH_COLORS.cardShadow,
};

export const hhMarketingStepPlaceholderSx = {
  background: HH_COLORS.track,
  borderRadius: 1,
};

export const hhMarketingStepBubbleMutedSx = {
  background: HH_COLORS.inset,
  borderRadius: 2,
};

export const hhMarketingStepBadgeSx = (color) => ({
  position: 'absolute',
  top: 20,
  right: 20,
  width: 28,
  height: 28,
  borderRadius: '8px',
  background: color,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '-0.02em',
});

export const hhMarketingFeatureCardSx = {
  ...hhMarketingCardSx,
  height: '100%',
  p: 3,
  textAlign: 'center',
};

export const hhMarketingFeatureIconSx = {
  width: 48,
  height: 48,
  borderRadius: '14px',
  background: HH_COLORS.inset,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: HH_COLORS.fg,
  fontSize: '1.25rem',
  mb: 2,
  mx: 'auto',
};

export const hhMarketingPricingCardSx = (popular = false) => ({
  ...hhMarketingCardSx,
  height: '100%',
  textAlign: 'center',
  position: 'relative',
  ...(popular && {
    border: `1.5px solid ${HH_COLORS.teal}`,
    boxShadow: '0 0 0 4px color-mix(in srgb, var(--hh-teal) 8%, transparent)',
  }),
});

export const hhMarketingPricingIconSx = {
  width: 52,
  height: 52,
  borderRadius: '16px',
  background: HH_COLORS.inset,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mx: 'auto',
  mb: 2.5,
};

export const hhMarketingStoryCardSx = {
  ...hhMarketingCardSx,
  height: '100%',
};

export const hhMarketingQuoteSx = {
  p: 2.5,
  borderRadius: '14px',
  background: HH_COLORS.inset,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  position: 'relative',
  mb: 2.5,
};

export const hhMarketingChipSx = {
  height: 24,
  fontSize: '0.7rem',
  fontWeight: 600,
  background: HH_COLORS.inset,
  color: HH_COLORS.fg3,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  '& .MuiChip-label': { px: 1.25 },
};

export const hhMarketingStatusChipSx = (category) => ({
  height: 26,
  fontSize: '0.75rem',
  fontWeight: 600,
  background:
    category === 'married'
      ? 'color-mix(in srgb, #10b981 12%, transparent)'
      : category === 'engaged'
        ? 'color-mix(in srgb, #f59e0b 12%, transparent)'
        : 'color-mix(in srgb, var(--hh-teal) 12%, transparent)',
  color:
    category === 'married'
      ? '#10b981'
      : category === 'engaged'
        ? '#d97706'
        : HH_COLORS.teal,
  border: 'none',
});

export const hhMarketingCtaPaperSx = {
  p: { xs: 5, md: 6 },
  textAlign: 'center',
  backgroundColor: HH_COLORS.card,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: '24px',
  boxShadow: HH_COLORS.cardShadow,
};

export const hhMarketingTabsPaperSx = {
  p: 1,
  backgroundColor: HH_COLORS.inset,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: '16px',
  boxShadow: HH_COLORS.cardShadow,
};

export const hhMarketingTabSelectedSx = {
  color: HH_COLORS.fg,
  fontWeight: 600,
  backgroundColor: HH_COLORS.card,
  boxShadow: HH_COLORS.cardShadow,
};

export const hhMarketingBillingToggleSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  mt: 2,
  mb: 2,
  p: 2,
  background: HH_COLORS.card,
  border: `1px solid ${HH_COLORS.cardBorder}`,
  borderRadius: '999px',
  maxWidth: 320,
  mx: 'auto',
  boxShadow: HH_COLORS.cardShadow,
};

/** Site footer — border-first, matches marketing surfaces */
export const hhFooterSx = {
  mt: 'auto',
  borderTop: `1px solid ${HH_COLORS.cardBorder}`,
  background: HH_COLORS.bg,
  boxShadow: 'none',
};

export const hhFooterInnerSx = {
  maxWidth: 1120,
  mx: 'auto',
  px: { xs: 3, md: 4 },
  py: { xs: 5, md: 6 },
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
  gap: { xs: 4, md: 8 },
  alignItems: 'start',
};

export const hhFooterBrandSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  maxWidth: 360,
};

export const hhFooterLogoRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
};

export const hhFooterLogoTextSx = {
  fontFamily: "'Playfair Display', serif",
  fontWeight: 700,
  fontSize: '1.25rem',
  letterSpacing: '-0.02em',
  color: HH_COLORS.fg,
  lineHeight: 1.2,
};

export const hhFooterTaglineSx = {
  color: HH_COLORS.fg3,
  fontSize: '0.875rem',
  lineHeight: 1.55,
  fontWeight: 400,
};

export const hhFooterNavGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: { xs: 3, sm: 4 },
};

export const hhFooterNavLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: HH_COLORS.teal,
  mb: 1.25,
};

export const hhFooterLinksSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 0.25,
};

export const hhFooterLinkSx = {
  color: HH_COLORS.fg3,
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  px: 1.25,
  py: 0.625,
  ml: -1.25,
  borderRadius: '10px',
  transition: 'color 0.15s ease, background 0.15s ease',
  '&:hover': {
    color: HH_COLORS.fg,
    background: 'color-mix(in srgb, var(--hh-teal) 6%, transparent)',
  },
};

export const hhFooterBottomSx = {
  borderTop: `1px solid ${HH_COLORS.cardBorder}`,
  py: 2.5,
  px: { xs: 3, md: 4 },
  textAlign: 'center',
};

export const hhFooterCopyrightSx = {
  color: HH_COLORS.fg3,
  fontSize: '0.8rem',
  fontWeight: 400,
  m: 0,
};
